import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, alpha, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, MenuItem, Slider,
  Stack, TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import { createEngagement, deleteEngagement, listEngagements, updateEngagement } from 'api/features';
import { GREEN } from './scanUi';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const Pill = ({ color, children }) => <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, px: 1.3, py: 0.5, borderRadius: 99, bgcolor: alpha(color, 0.16), color, whiteSpace: 'nowrap' }}>{children}</Typography>;

const STATUSES = { planned: { label: 'PLANNED', color: '#94a3b8' }, active: { label: 'ACTIVE', color: '#38bdf8' }, completed: { label: 'COMPLETED', color: GREEN }, cancelled: { label: 'CANCELLED', color: '#64748b' } };
const METHODOLOGIES = ['OWASP WSTG', 'PTES', 'NIST 800-115', 'OWASP MSTG', 'OWASP API Top 10', 'Custom'];
const BLANK = { title: '', client: '', methodology: 'OWASP WSTG', status: 'planned', start_date: '', end_date: '', progress: 0, scope: '', rules_of_engagement: '' };

const fail = (e, fallback) => {
  const d = e?.response?.data;
  if (d && typeof d === 'object') return Object.values(d).flat().join(' ') || fallback;
  return e?.message || fallback;
};
const period = (e) => (e.start_date || e.end_date ? `${e.start_date || '…'} to ${e.end_date || '…'}` : 'No dates set');

export default function EngagementsPage() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [show, setShow] = useState('all');
  const [editing, setEditing] = useState(null); // null closed, 'new', or the engagement being edited
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await listEngagements();
      setRows(Array.isArray(r) ? r : r.results || []);
    } catch (e) { setError(fail(e, 'Could not load engagements.')); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(BLANK); setEditing('new'); setError(''); };
  const openEdit = (e) => {
    setForm({ ...BLANK, ...e, start_date: e.start_date || '', end_date: e.end_date || '' });
    setEditing(e); setError('');
  };
  const set = (patch) => setForm((p) => ({ ...p, ...patch }));

  const save = async () => {
    setSaving(true); setError('');
    const body = { ...form, start_date: form.start_date || null, end_date: form.end_date || null };
    try {
      if (editing === 'new') await createEngagement(body); else await updateEngagement(editing.id, body);
      setEditing(null); await load();
    } catch (e) { setError(fail(e, 'Could not save the engagement.')); }
    setSaving(false);
  };
  const remove = async () => {
    const target = confirm; setConfirm(null);
    try { await deleteEngagement(target.id); await load(); } catch (e) { setError(fail(e, 'Could not delete the engagement.')); }
  };

  const list = (rows || []).filter((e) => show === 'all' || e.status === show);
  const count = (s) => (rows || []).filter((e) => e.status === s).length;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Engagements</Typography>
          <Typography color="text.secondary">
            {rows === null ? 'Loading…' : rows.length === 0 ? 'Keep each client assessment in one place: scope, rules, dates and progress' : `${count('active')} active, ${count('planned')} planned, ${count('completed')} completed`}
          </Typography>
        </Box>
        <Button variant="contained" onClick={openNew} sx={{ height: 44, fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>New engagement</Button>
      </Stack>

      {error && !editing && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {(rows || []).length > 0 && (
        <ToggleButtonGroup size="small" exclusive value={show} onChange={(_, v) => v && setShow(v)} sx={{ mb: 2.5 }}>
          <ToggleButton value="all">All</ToggleButton>
          {Object.entries(STATUSES).map(([id, s]) => <ToggleButton key={id} value={id}>{s.label.charAt(0) + s.label.slice(1).toLowerCase()}</ToggleButton>)}
        </ToggleButtonGroup>
      )}

      {rows !== null && list.length === 0 ? (
        <Box sx={{ ...panel, p: 5, textAlign: 'center' }}>
          <Typography fontWeight={600}>{rows.length === 0 ? 'No engagements yet' : 'Nothing with that status'}</Typography>
          {rows.length === 0 && <Typography color="text.secondary" sx={{ mb: 2 }}>Create one for each client or assessment you run.</Typography>}
        </Box>
      ) : (
        <Box sx={{ ...panel, overflow: 'hidden' }}>
          {list.map((e, i) => {
            const st = STATUSES[e.status] || STATUSES.planned;
            return (
              <Stack key={e.id} direction="row" alignItems="center" flexWrap="wrap" gap={2} sx={{ px: 2.5, py: 1.8, borderTop: i ? 1 : 0, borderColor: 'divider', borderLeft: 4, borderLeftColor: st.color }}>
                <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
                  <Typography fontWeight={700}>{e.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{[e.client, e.methodology].filter(Boolean).join(' · ') || 'No client set'}</Typography>
                  <Typography variant="caption" color="text.secondary">{period(e)}</Typography>
                </Box>
                <Box sx={{ width: 150 }}>
                  <LinearProgress variant="determinate" value={Math.min(100, e.progress || 0)} sx={{ height: 6, borderRadius: 99, bgcolor: alpha(GREEN, 0.12), '& .MuiLinearProgress-bar': { bgcolor: GREEN } }} />
                  <Typography variant="caption" color="text.secondary">{e.progress || 0}% done</Typography>
                </Box>
                <Pill color={st.color}>{st.label}</Pill>
                <Stack direction="row" gap={0.5}>
                  <Button size="small" color="inherit" onClick={() => openEdit(e)}>Edit</Button>
                  <Button size="small" color="error" onClick={() => setConfirm(e)}>Delete</Button>
                </Stack>
              </Stack>
            );
          })}
        </Box>
      )}

      <Dialog open={Boolean(editing)} onClose={() => !saving && setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editing === 'new' ? 'New engagement' : 'Edit engagement'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField fullWidth size="small" label="Name" placeholder="Q2 2026 external assessment" value={form.title} onChange={(e) => set({ title: e.target.value })} />
            <TextField fullWidth size="small" label="Client" placeholder="Acme Corp" value={form.client} onChange={(e) => set({ client: e.target.value })} />
            <Stack direction="row" spacing={1.5}>
              <TextField fullWidth select size="small" label="Methodology" value={form.methodology} onChange={(e) => set({ methodology: e.target.value })}>
                {METHODOLOGIES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
              <TextField fullWidth select size="small" label="Status" value={form.status} onChange={(e) => set({ status: e.target.value })}>
                {Object.entries(STATUSES).map(([id, s]) => <MenuItem key={id} value={id}>{s.label.charAt(0) + s.label.slice(1).toLowerCase()}</MenuItem>)}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={1.5}>
              <TextField fullWidth size="small" type="date" label="Start" InputLabelProps={{ shrink: true }} value={form.start_date} onChange={(e) => set({ start_date: e.target.value })} />
              <TextField fullWidth size="small" type="date" label="End" InputLabelProps={{ shrink: true }} value={form.end_date} onChange={(e) => set({ end_date: e.target.value })} />
            </Stack>
            <Box sx={{ px: 1 }}>
              <Typography variant="caption" color="text.secondary">Progress: {form.progress}%</Typography>
              <Slider size="small" value={form.progress} min={0} max={100} step={5} onChange={(_, v) => set({ progress: v })} sx={{ color: GREEN }} />
            </Box>
            <TextField fullWidth multiline minRows={2} size="small" label="Scope" placeholder="What is in scope: domains, ranges, applications" value={form.scope} onChange={(e) => set({ scope: e.target.value })} />
            <TextField fullWidth multiline minRows={2} size="small" label="Rules of engagement" placeholder="Excluded hosts, testing windows, contacts" value={form.rules_of_engagement} onChange={(e) => set({ rules_of_engagement: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" disabled={saving || !form.title.trim()} onClick={save} sx={{ fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)}>
        <DialogTitle>Delete {confirm?.title}?</DialogTitle>
        <DialogContent><Typography color="text.secondary">The engagement record, its scope and its rules are removed. Your findings are not touched.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setConfirm(null)}>Keep it</Button>
          <Button color="error" variant="contained" onClick={remove}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
